import {
  AppBar,
  Avatar,
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  TableCell,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Search from '@material-ui/icons/Search';
import { cloneDeep, debounce } from 'lodash-es';
import MaterialTable from 'material-table';
import React from 'react';
import update from 'react-addons-update';

import {
  deleteUserInvite,
  getUsersFromUserToPublisherAssociation,
  inviteUser,
  IUserFromUserToPublisherAssociation,
  IUserInvite,
  signupUserManualResellerID,
  updateTimezoneForUser,
} from '~/src/dash/api/console-api';
import { IPermission, IPermissionsTemplate, IRole } from '~/src/dash/api/permissions/model';
import { createRole, deleteUser, updateRoleForUser } from '~/src/dash/api/permissions/service';
import { getPublishersNames } from '~/src/dash/api/publishers/publishers_methods';
import { IObject } from '~/src/dash/api/resource-transforms';
import { createUrl, IHttpResponse, restGet } from '~/src/dash/api/rest';
import { AsyncButtonV2 } from '~/src/dash/components/button';
import {
  FormItemTypePassword,
  FormItemTypeText,
  IFormData,
  IFormItem,
  RenderForm,
} from '~/src/dash/components/form';
import { IModel } from '~/src/dash/components/model';
import { ModelRolesManager } from '~/src/dash/components/model-roles-manager';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { MakeTabProps, TabPanel } from '~/src/dash/components/TabPanel';
import { REGEX_EMAIL } from '~/src/dash/globals/constants';
import { TIMEZONES } from '~/src/dash/globals/timezone';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { getResellerID } from '~/src/dash/resellers/utils/importResellerConfig';
import { getDemoUsernames, isRoku } from '~/src/dash/resellers/utils/importResellerConfig';
import { reload, RouteTeamSettings } from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { backgroundColor } from '~/src/dash/utils/material-dashboard-react';
import { obfuscateUserInfo } from '~/src/dash/utils/obfuscate';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRole, ROLE_RESELLER, ROLE_SUPER } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import { hasErrors, IErrors, transformErrors, validatorFactory } from '~/src/dash/utils/validator';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

declare module 'material-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface Column<RowData extends object> {
    width?: string;
  }
}

// validates the create publisher form
const validate = validatorFactory({
  FirstName(val: any): string {
    if (val === undefined || val.length === 0) {
      return 'Please enter a first name.';
    }
    return '';
  },
  LastName(val: any): string {
    if (val === undefined || val.length === 0) {
      return 'Please enter a last name.';
    }
    return '';
  },
  Company(val: any): string {
    if (val === undefined || val.length === 0) {
      return 'Please enter a company.';
    }
    return '';
  },
  Domain(val: any): string {
    if (val === undefined || val.length === 0) {
      return 'Please enter a domain.';
    }
    return '';
  },
  Email(val: any): string {
    if (val === undefined || (!!val && !REGEX_EMAIL.test(val))) {
      return 'Please enter valid email.';
    }
    return '';
  },
  Password(val: any): string {
    if (val === undefined || (!!val && val.length < 8)) {
      return 'Your password needs to be at least 8 characters long.';
    }
    return '';
  },
  Password2(val: any, _key: string, obj: IObject): string {
    if (val !== obj.Password) {
      return 'Your passwords did not match';
    } else if (val === undefined || (!!val && val.length < 8)) {
      return 'Your password needs to be at least 8 characters long.';
    }
    return '';
  },
});

// State, Props, component...
interface IState extends IBaseState {
  Email: string;
  usersFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation[];
  usersInvite: IUserInvite[];

  inviting: boolean;
  loading: boolean;
  countInviting: number;
  countLoading: number;

  snackbarMessage: string;
  snackbarError: boolean;

  permissionsTemplate: IPermissionsTemplate;
  availableRoles: IRole[];
  openCreateRoleDialog: boolean;
  openUpdateRoleForUserDialog: boolean;
  openUpdateRoleForInviteDialog: boolean;
  openUpdateTimezoneForUserDialog: boolean;
  openDeleteUsersDialog: boolean;
  selectedUsersFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation[];
  newRoleForUser: number;
  newTimezoneForUser: string;
  newRoleForInvite: number;
  tabIndex: number;
  invitePublisher: IModel;
  inviteAvailableRoles: IRole[];
  userInviteLoading: boolean;

  // createPublisher
  data: IFormData;
  countSignup: number;
  showLoadingWheel: boolean;
  errors: IErrors;
  isNewRokuPublisher: boolean;
}

export const TeamSettingsView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4,
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '800px',
        },
        userButton: {
          float: 'right',
          fontFamily: 'AvenirNext-Regular',
          fontSize: '0.8rem',
          color: '#C1C6CC',
          backgroundColor: '#EAEAEA',
          padding: '5px 20px',
          marginTop: '5px',
          borderRadius: '15px',
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      Email: '',
      usersFromUserToPublisherAssociation: [] as IUserFromUserToPublisherAssociation[],
      usersInvite: [] as IUserInvite[],
      inviting: false,
      loading: false,
      countInviting: 0,
      countLoading: 0,
      snackbarMessage: '',
      snackbarError: false,
      permissionsTemplate: undefined as unknown as IPermissionsTemplate,
      availableRoles: undefined as unknown as IRole[],
      openCreateRoleDialog: false,
      openUpdateRoleForUserDialog: false,
      openUpdateRoleForInviteDialog: false,
      openUpdateTimezoneForUserDialog: false,
      openDeleteUsersDialog: false,
      selectedUsersFromUserToPublisherAssociation: [] as IUserFromUserToPublisherAssociation[],
      newRoleForUser: -1,
      newTimezoneForUser: '',
      newRoleForInvite: 4,
      tabIndex: 0,
      invitePublisher: this.props.session.publisher,
      inviteAvailableRoles: undefined as unknown as IRole[],
      userInviteLoading: false,
      // create publisher
      data: {} as IFormData,
      countSignup: 0,
      showLoadingWheel: false,
      errors: {} as IErrors,
      isNewRokuPublisher: isRoku(),
    } as IState;

    _createPublisher = debounce((countSignup: number) => {
      if (countSignup !== this.state.countSignup) {
        return;
      }
      signupUserManualResellerID({
        body: Object.assign(
          {
            form: 'prebid',
            email: this.state.data.Email,
            password: this.state.data.Password,
            password2: this.state.data.Password2,
            first_name: this.state.data.FirstName,
            last_name: this.state.data.LastName,
            name: this.state.data.Company,
            domain: this.state.data.Domain,
            region: 'us-east-1',
            reseller_id: this.state.isNewRokuPublisher ? 4 : getResellerID(),
            roku_seat_id: this.state.isNewRokuPublisher ? this.state.data.RokuSeatID : '',
            roku_provider_channel_id: this.state.isNewRokuPublisher
              ? this.state.data.RokuProviderChannelID
              : '',
          },
          {},
        ),
      })
        .then(
          () => {
            if (countSignup !== this.state.countSignup) {
              return;
            }
            this.setState({
              snackbarMessage: 'Publisher successfully created.',
              showLoadingWheel: false,
            });
          },
          ({ msg }: { msg: string }) => {
            if (countSignup !== this.state.countSignup) {
              return;
            }
            console.error(msg);
            this.setState({
              showLoadingWheel: false,
              snackbarMessage: `Error creating publisher: ${msg}`,
              snackbarError: true,
            });
          },
        )
        .catch(() => {
          this.setState({
            snackbarMessage: 'Error creating publisher.',
            snackbarError: true,
          });
        });
    }, 200);

    _getUsersFromUserToPublisherAssociation = debounce((countLoading: number) => {
      if (countLoading !== this.state.countLoading) {
        this.setState({
          loading: false,
        });
        return;
      }
      getUsersFromUserToPublisherAssociation(this.props.session.publisher.ID)
        .then(({ usersFromUserToPublisherAssociation, usersInvite }) => {
          this.setState({
            loading: false,
            usersFromUserToPublisherAssociation,
            usersInvite,
          });
        })
        .catch(() => {
          this.setState({
            loading: false,
            snackbarMessage: 'Getting user list failed. Please refresh the page and try again.',
            snackbarError: true,
          });
        });
    }, 500);

    // Get pending user invites for selected publisher for inviting
    _getUserInvitesForPublisher = debounce((countLoading: number) => {
      if (countLoading !== this.state.countLoading) {
        this.setState({
          userInviteLoading: false,
        });
        return;
      }
      getUsersFromUserToPublisherAssociation(this.state.invitePublisher.ID)
        .then(({ usersInvite }) => {
          this.setState({
            userInviteLoading: false,
            usersInvite,
          });
        })
        .catch(() => {
          this.setState({
            userInviteLoading: false,
            snackbarMessage: 'Getting user list failed. Please refresh the page and try again.',
            snackbarError: true,
          });
        });
    }, 500);

    _inviteUser = debounce((countInviting: number) => {
      if (countInviting !== this.state.countInviting) {
        return;
      }
      if (!REGEX_EMAIL.test(this.state.Email)) {
        this.setState({
          inviting: false,
          snackbarMessage: 'Please enter valid email and try again later.',
          snackbarError: true,
        });
        return;
      }
      // Check if selected role is in available roles
      if (
        this.state.inviteAvailableRoles.find(
          (role: IRole) => role.id === this.state.newRoleForInvite,
        ) === undefined
      ) {
        this.setState({
          inviting: false,
          snackbarMessage: 'Please enter valid role for the invited user and try again.',
          snackbarError: true,
        });
        return;
      }

      inviteUser({
        body: {
          ishb: true,
          email: this.state.Email,
          permissions_role_id: this.state.newRoleForInvite,
          publisher_id: this.state.invitePublisher.ID,
        },
        query: {
          publisher_id: this.props.user.publisher.ID,
        },
      })
        .then(() => {
          const count = this.state.countLoading;
          this.setState(
            {
              Email: '',
              inviting: false,
            },
            () => {
              this.getUserInvitesForPublisher(count);
              this.getUsersFromUserToPublisherAssociation();
            },
          );
        })
        .catch((response: any) => {
          this.setState({
            inviting: false,
            snackbarMessage: response.message || 'Inviting user failed. Please try again.',
            snackbarError: true,
          });
        });
    }, 500);

    componentDidMount() {
      this.getUsersFromUserToPublisherAssociation();
      this.getPermissionTemplate();
      this.getAvailableRoles();
      this.getAvailableInviteRoles(this.props.session.publisher);
    }

    getPermissionTemplate = () => {
      const permissionsTemplate = cloneDeep(this.props.user.permissionTemplate);
      if (isRoku()) {
        permissionsTemplate.root.permissions = permissionsTemplate.root.permissions.filter(
          (permission: IPermission) => {
            return !['campaign', 'network', 'audience_segment'].includes(permission.name);
          },
        );
      }
      this.setState({
        permissionsTemplate,
      });
    };

    getAvailableRoles = () => {
      const reqUrl = createUrl('/roles', this.props.session.publisher.ID);

      restGet<IRole[]>(reqUrl, {})
        .then((val: IHttpResponse<IRole[]>) => {
          this.setState({
            availableRoles: val.parsedBody as IRole[],
          });
        })
        .catch(() => {
          this.setState({
            availableRoles: [] as IRole[],
          });
        });
    };

    getAvailableInviteRoles = (publisher: IModel) => {
      this.setState({
        loading: true,
      });
      const reqUrl = createUrl('/roles', publisher.ID);

      restGet<IRole[]>(reqUrl, {})
        .then((val: IHttpResponse<IRole[]>) => {
          this.setState({
            inviteAvailableRoles: val.parsedBody as IRole[],
            loading: false,
          });
        })
        .catch(() => {
          this.setState({
            inviteAvailableRoles: [] as IRole[],
            loading: false,
          });
        });
    };

    setInviteState = (publisher: IModel) => {
      this.setState({
        invitePublisher: publisher,
        newRoleForInvite: 4,
      });
      this.getAvailableInviteRoles(publisher);
      this.getUserInvitesForPublisher(this.state.countLoading);
    };

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        [target.name]: target.value,
      });
    };

    inviteUser = () => {
      const countInviting = this.state.countInviting + 1;
      this.setState(
        {
          inviting: true,
          countInviting,
        },
        () => {
          this._inviteUser(countInviting);
        },
      );
    };

    getUsersFromUserToPublisherAssociation() {
      const countLoading = this.state.countLoading + 1;
      this.setState(
        {
          loading: true,
          countLoading,
        },
        () => {
          this._getUsersFromUserToPublisherAssociation(countLoading);
        },
      );
    }

    getUserInvitesForPublisher(countLoadingInput: number) {
      const countLoading = countLoadingInput ? countLoadingInput + 1 : this.state.countLoading + 1;
      this.setState(
        {
          userInviteLoading: true,
          countLoading,
        },
        () => {
          this._getUserInvitesForPublisher(countLoading);
        },
      );
    }

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleToggleModal = (key: string, value: boolean): void => {
      this.setState({
        [key]: value,
      });
    };

    createRole = () => {
      createRole(this.state.newRoleName, parseInt(this.props.session.publisher.ID, 10)).then(
        ([role, err]) => {
          if (err !== undefined) {
            this.setState({
              snackbarMessage: 'Error during role creation: ' + err.toString(),
              snackbarError: true,
            });
          } else {
            if (role !== undefined) {
              this.setState({
                snackbarMessage: 'Role ' + role.name + ' successfully created!',
                snackbarError: false,
              });
            }
            this.getAvailableRoles();
          }
        },
      );
      this.handleToggleModal('openCreateRoleDialog', false);
    };

    // TODO make component instead
    renderCreateRoleDialog = () => {
      return (
        <Dialog
          open={this.state.openCreateRoleDialog}
          onClose={() => this.handleToggleModal('openCreateRoleDialog', false)}
          aria-labelledby='form-dialog-title'
        >
          <DialogTitle id='form-dialog-title'>Create Role</DialogTitle>
          <DialogContent>
            <DialogContentText>Enter the Name of the Role</DialogContentText>
            <TextField
              autoFocus
              margin='dense'
              id='newRoleName'
              name='newRoleName'
              label='role name'
              type='text'
              fullWidth
              onChange={this.handleChange}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleToggleModal('openCreateRoleDialog', false)}
              color='primary'
            >
              Cancel
            </Button>
            <Button onClick={this.createRole} color='primary'>
              Create
            </Button>
          </DialogActions>
        </Dialog>
      );
    };

    handleUpdateRole = () => {
      this.state.selectedUsersFromUserToPublisherAssociation.forEach(
        (u: IUserFromUserToPublisherAssociation) => {
          updateRoleForUser(
            this.state.newRoleForUser,
            u.UserID,
            parseInt(this.props.session.publisher.ID, 10),
          ).then(([role, err]) => {
            if (err !== undefined) {
              this.setState({
                snackbarMessage: err.toString(),
                snackbarError: true,
              });
            } else {
              this.getUsersFromUserToPublisherAssociation();
              if (role !== undefined) {
                this.setState({
                  snackbarMessage: 'Role successfully assigned!',
                  snackbarError: false,
                });
              }
            }
          });
        },
      );
      this.handleToggleModal('openUpdateRoleForUserDialog', false);
    };

    // Deletes a user from state.usersFromUserToPublisherAssociation by its ID.
    deleteUserFromUserToPublisherAssociationFromStateWithID = (userID: string) => {
      // Find the user index in the state.
      const userIndex = this.state.usersFromUserToPublisherAssociation.findIndex(
        (userFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation) =>
          userFromUserToPublisherAssociation.UserID === parseInt(userID, 10),
      );
      if (userIndex === undefined) {
        return; // User not found, nothing to do.
      }

      // Remove the user from the state.
      this.setState({
        usersFromUserToPublisherAssociation: update(
          this.state.usersFromUserToPublisherAssociation,
          {
            $splice: [[userIndex, 1]],
          },
        ),
      });
    };

    // Deletes all selected users. Called by the "Yes" button of the confirmation pop-up.
    // INFO: This function calls itself recursively. We do that because deleteUser is an async function and we cannot
    //       await it here. But we also don't want to delete the next user until the previous one hasn't been deleted (or failed).
    handleDeleteUsers = (selectedUserIndex?: number) => {
      // Undefined selectedUserIndex means the function is executed for the first time (no recursion).
      if (selectedUserIndex === undefined) {
        // Close the confirmation pop-up.
        this.handleToggleModal('openDeleteUsersDialog', false);
        // Initialize the index to 0.
        selectedUserIndex = 0;
      }

      // Check if we finished deleting all the selected users.
      if (selectedUserIndex >= this.state.selectedUsersFromUserToPublisherAssociation.length) {
        // Display a success snackbar error message.
        this.setState({
          snackbarMessage: 'User(s) successfully deleted!',
          snackbarError: false,
        });
      }

      // We still have user(s) to delete. Delete the user under the selectedUserIndex and call the function recursively.
      const user = this.state.selectedUsersFromUserToPublisherAssociation[selectedUserIndex];
      deleteUser(user.UserID, parseInt(this.props.session.publisher.ID, 10)).then(
        ([success, err]) => {
          // Check if the deletion succeeded.
          if (!success) {
            // The deletion failed.
            // Display the error snackbar.
            this.setState({
              snackbarError: true,
              snackbarMessage:
                err !== undefined ? err.toString() : `Cannot delete user ${user.UserID}.`,
            });
          } else {
            // The deletion succeeded.
            // Delete the user from the state.
            this.deleteUserFromUserToPublisherAssociationFromStateWithID(user.UserID.toString());
            // Call the function on the next index (recursively).
            this.handleDeleteUsers((selectedUserIndex || 0) + 1);
          }
        },
      );
    };

    // Update the selected users timezone
    updateTimezone = (timezone: string) => {
      this.state.selectedUsersFromUserToPublisherAssociation.forEach(
        (u: IUserFromUserToPublisherAssociation) => {
          updateTimezoneForUser(
            timezone,
            u.UserID,
            parseInt(this.props.session.publisher.ID, 10),
          ).then(([role, err]) => {
            if (err !== undefined) {
              this.setState({
                snackbarMessage: err.toString(),
                snackbarError: true,
              });
            } else {
              this.getUsersFromUserToPublisherAssociation();
              if (role !== undefined) {
                this.setState({
                  snackbarMessage: 'Timezone successfully assigned!',
                  snackbarError: false,
                });
              }
            }
          });
        },
      );
      this.handleToggleModal('openUpdateTimezoneForUserDialog', false);
      // Reload page so that the timezone is updated on other pages
      reload(RouteTeamSettings, {
        publisherID: this.props.session.publisher.ID,
      });
    };

    handleUpdateDefaultTimezone = () => {
      this.updateTimezone('');
    };

    handleUpdateTimezone = () => {
      this.updateTimezone(this.state.newTimezoneForUser);
    };

    renderChangeRoleForUserDialog = () => {
      return (
        <Dialog
          open={this.state.openUpdateRoleForUserDialog}
          onClose={() => this.handleToggleModal('openUpdateRoleForUserDialog', false)}
          aria-labelledby='form-dialog-title'
        >
          <DialogTitle id='form-dialog-title'>Assign Role for user(s)</DialogTitle>
          <DialogContent>
            <DialogContentText>Choose the new Role:</DialogContentText>
            <Select
              autoFocus
              margin='dense'
              id='newRoleForUser'
              name='newRoleForUser'
              label='role name'
              type='text'
              fullWidth
              value={this.state.newRoleForUser}
              onChange={this.handleChange}
            >
              {(this.state.availableRoles || []).map((role: IRole) => {
                return <MenuItem value={role.id}>{role.name}</MenuItem>;
              })}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleToggleModal('openUpdateRoleForUserDialog', false)}
              color='primary'
            >
              Cancel
            </Button>
            <Button onClick={this.handleUpdateRole} color='primary'>
              Assign Role
            </Button>
          </DialogActions>
        </Dialog>
      );
    };

    renderDeleteUsersDialog = () => {
      return (
        <Dialog
          open={this.state.openDeleteUsersDialog}
          onClose={() => this.handleToggleModal('openDeleteUsersDialog', false)}
          aria-labelledby='form-dialog-title'
        >
          <DialogTitle id='form-dialog-title'>Delete user(s)</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this/these user(s)?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleToggleModal('openDeleteUsersDialog', false)}
              color='primary'
            >
              No
            </Button>
            <Button onClick={() => this.handleDeleteUsers()} color='primary'>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      );
    };

    renderChangeTimezoneForUserDialog = () => {
      return (
        <Dialog
          open={this.state.openUpdateTimezoneForUserDialog}
          onClose={() => this.handleToggleModal('openUpdateTimezoneForUserDialog', false)}
          aria-labelledby='form-dialog-title'
        >
          <DialogTitle id='form-dialog-title'>Assign Timezone for user</DialogTitle>
          <DialogContent>
            <DialogContentText>Choose the new Timezone:</DialogContentText>
            <Select
              autoFocus
              margin='dense'
              id='newTimezoneForUser'
              name='newTimezoneForUser'
              label='role name'
              type='text'
              fullWidth
              value={this.state.newTimezoneForUser}
              onChange={this.handleChange}
            >
              {(TIMEZONES || []).map((timezone: any) => {
                return <MenuItem value={timezone.label}>{timezone.label}</MenuItem>;
              })}
            </Select>
          </DialogContent>
          <Button onClick={this.handleUpdateDefaultTimezone} color='primary'>
            Set to Default
          </Button>
          <Button onClick={this.handleUpdateTimezone} color='primary'>
            Assign Timezone
          </Button>
          <DialogActions>
            <Button
              onClick={() => this.handleToggleModal('openUpdateTimezoneForUserDialog', false)}
              color='primary'
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      );
    };

    renderChangeRoleForInviteDialog = () => {
      return (
        <Dialog
          open={this.state.openUpdateRoleForInviteDialog}
          onClose={() => this.handleToggleModal('openUpdateRoleForInviteDialog', false)}
          aria-labelledby='form-dialog-title'
        >
          <DialogTitle id='form-dialog-title'>Assign Role for invite</DialogTitle>
          <DialogContent>
            <DialogContentText>Choose the new Role:</DialogContentText>
            <Select
              autoFocus
              margin='dense'
              id='newRoleForInvite'
              name='newRoleForInvite'
              label='role name'
              type='text'
              fullWidth
              value={this.state.newRoleForInvite}
              onChange={this.handleChange}
            >
              {(this.state.inviteAvailableRoles || []).map((role: IRole) => {
                return <MenuItem value={role.id}>{role.name}</MenuItem>;
              })}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleToggleModal('openUpdateRoleForInviteDialog', false)}
              color='primary'
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      );
    };

    displaySuccess = (str: string) => {
      this.setState({
        snackbarMessage: str,
        snackbarError: false,
      });
    };

    displayError = (str: string) => {
      this.setState({
        snackbarMessage: str,
        snackbarError: true,
      });
    };

    renderAvatar = (user: IUserFromUserToPublisherAssociation) => {
      if (user.FirstName === '(Pending)') {
        return <Avatar>?</Avatar>;
      }
      return <Avatar>{`${user.FirstName.charAt(0)}${user.LastName.charAt(0)}`}</Avatar>;
    };

    renderDisplayedName = (user: IUserFromUserToPublisherAssociation) => {
      return (
        <p>
          {user.FirstName} {user.LastName}
        </p>
      );
    };

    renderDeleteIcon = (userInvite: IUserInvite) => {
      return (
        <Button onClick={() => this.deleteUserInvite(userInvite)}>
          <DeleteIcon />
        </Button>
      );
    };

    deleteUserInvite = (userInvite: IUserInvite) => {
      deleteUserInvite({
        query: {
          publisher_id: this.state.invitePublisher.ID,
          user_invite_id: userInvite.ID,
        },
      })
        .then(() => this.getUserInvitesForPublisher(this.state.countLoading))
        .catch(() =>
          this.setState({ snackbarError: true, snackbarMessage: 'Error deleting user invite' }),
        );
    };

    handleChangeRoleForUsers = (
      selectedUsersFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation[],
    ) => {
      this.setState(
        {
          selectedUsersFromUserToPublisherAssociation,
          newRoleForUser: -1,
        },
        () => this.handleToggleModal('openUpdateRoleForUserDialog', true),
      );
    };

    handleChangeTimezoneForUsers = (
      selectedUsersFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation[],
    ) => {
      this.setState(
        {
          selectedUsersFromUserToPublisherAssociation,
        },
        () => this.handleToggleModal('openUpdateTimezoneForUserDialog', true),
      );
    };

    // Create Publisher Helper Functions
    // submits new publisher form
    handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      this.createPublisher();
    };

    // updates the form and validates
    onUpdate = (data: IFormData) => {
      if (hasErrors(this.state.errors)) {
        this.validateForm();
      }
      this.setState({ data });
    };

    // validates the form fields and sets errors to be displayed
    validateForm = (): boolean => {
      const errors = validate(this.state.data);
      if (hasErrors(errors)) {
        this.setState({
          errors,
        });
        return false;
      }
      this.setState({
        errors: {} as IErrors,
      });
      return true;
    };

    // validates and creates a new publisher
    createPublisher = () => {
      if (!this.validateForm()) {
        this.setState({
          snackbarMessage:
            'Creating new publisher failed. Please fix the errors and try again later.',
          snackbarError: true,
        });
        return;
      }
      const countSignup = this.state.countSignup + 1;
      this.setState(
        {
          showLoadingWheel: true,
          countSignup,
        },
        () => this._createPublisher(countSignup),
      );
    };

    renderRole = (hasRightToAssignRoles: boolean) => {
      return (user: IUserFromUserToPublisherAssociation) => {
        let displayedText: string;
        if (
          user.PermissionsRoleName !== null &&
          user.PermissionsRoleName !== undefined &&
          user.PermissionsRoleName.length > 0
        ) {
          displayedText = user.PermissionsRoleName;
        } else {
          displayedText = 'No role';
        }
        return (
          <ModelSecondaryButton
            disabled={user.UserID === undefined || !hasRightToAssignRoles}
            onClick={() => this.handleChangeRoleForUsers([user])}
          >
            {displayedText}
          </ModelSecondaryButton>
        );
      };
    };

    renderTimezone = (hasRightToAssignRole: boolean) => {
      return (user: IUserFromUserToPublisherAssociation) => {
        let displayedText: string;
        if (user.Timezone !== null && user.Timezone !== '') {
          displayedText = user.Timezone;
        } else {
          displayedText = 'Default';
        }
        return (
          <ModelSecondaryButton
            disabled={
              user.UserID === undefined ||
              (!hasRightToAssignRole && user.UserID.toString() !== this.props.user.user.ID)
            }
            onClick={() => this.handleChangeTimezoneForUsers([user])}
          >
            {displayedText}
          </ModelSecondaryButton>
        );
      };
    };

    renderInviteRoles = (hasRightToAssignRoles: boolean) => {
      if (this.state.inviteAvailableRoles !== undefined) {
        let roleName = 'Select Valid Role';
        this.state.inviteAvailableRoles.forEach((role: IRole) => {
          if (role.id === this.state.newRoleForInvite) {
            roleName = role.name;
          }
        });
        return (
          <ModelSecondaryButton
            disabled={!hasRightToAssignRoles}
            onClick={() => this.handleToggleModal('openUpdateRoleForInviteDialog', true)}
          >
            {roleName}
          </ModelSecondaryButton>
        );
      } else {
        return;
      }
    };

    customFilterForUserName = (term: string, row: IUserFromUserToPublisherAssociation): boolean => {
      return (row.FirstName.toLocaleLowerCase() + ' ' + row.LastName.toLocaleLowerCase()).includes(
        term.toLocaleLowerCase(),
      );
    };

    customFilterForRole = (term: string[], row: IUserFromUserToPublisherAssociation): boolean => {
      if (term.length === 0) {
        return true;
      }

      if (
        row.PermissionsRoleID === undefined ||
        row.PermissionsRoleID === null ||
        row.PermissionsRoleID === 0
      ) {
        // No Role
        return term.includes('0');
      }
      return term.includes(row.PermissionsRoleID.toString());
    };

    renderTableOfUsers = (
      usersFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation[],
    ) => {
      const rolesLookup = { 0: 'No Role' };
      for (const r of this.state.availableRoles || []) {
        // @ts-ignore
        rolesLookup[r.id] = r.name;
      }

      const hasRightToAssignRole = IS_AUTHORIZED(
        ['roles_admin'],
        this.props.user.user,
      ).isAuthorized;
      const isAdmin = hasRole(this.props.user.user, [ROLE_SUPER, ROLE_RESELLER]);
      const hasRightToDeleteUsers =
        IS_AUTHORIZED(['delete_users'], this.props.user.user).isAuthorized || isAdmin;

      let actions = undefined as any;

      if (hasRightToDeleteUsers) {
        actions = [
          {
            tooltip: 'Delete all selected user(s)',
            icon: DeleteIcon as any,
            onClick: (_: any, data: IUserFromUserToPublisherAssociation[]) => {
              this.setState(
                {
                  selectedUsersFromUserToPublisherAssociation: data,
                },
                () => this.handleToggleModal('openDeleteUsersDialog', true),
              );
            },
          },
        ];
      }

      return (
        <MaterialTable
          title='Users'
          columns={[
            {
              title: '',
              field: 'name',
              render: this.renderAvatar,
              filtering: false,
              width: '20px',
            },
            {
              title: 'Name',
              field: 'Firstname',
              render: this.renderDisplayedName,
              customFilterAndSearch: this.customFilterForUserName,
            },
            { title: 'Email', field: 'Email' },
            {
              title: 'Role',
              field: 'Role',
              lookup: rolesLookup,
              render: this.renderRole(hasRightToAssignRole),
              customFilterAndSearch: this.customFilterForRole,
            },
            {
              title: 'Timezone',
              field: 'Timezone',
              render: this.renderTimezone(hasRightToAssignRole),
            },
          ]}
          data={usersFromUserToPublisherAssociation}
          icons={{
            Filter: Search as any,
          }}
          options={{
            filtering: true,
            search: false,
            selection: hasRightToDeleteUsers,
            padding: 'dense',
            pageSize: 10,
            pageSizeOptions: [10, 15, 40],
          }}
          actions={actions}
          localization={{
            toolbar: {
              nRowsSelected: 'Delete {0} user(s)',
            },
          }}
        />
      );
    };

    renderTableOfUsersInvite = (userInvites: IUserInvite[], hasRightToAssignRole: boolean) => {
      const columns = hasRightToAssignRole
        ? [
            { title: 'Email', field: 'Email', searchable: true, width: '80%' },
            { title: 'Delete', field: 'Delete', render: this.renderDeleteIcon, width: '20%' },
          ]
        : [{ title: 'Email', field: 'Email' }];
      return (
        <MaterialTable
          title='Pending User Invite'
          columns={columns}
          data={userInvites}
          icons={{
            Filter: Search as any,
          }}
          options={{
            selection: false,
            padding: 'dense',
            pageSize: 10,
            pageSizeOptions: [10, 15, 40],
          }}
        />
      );
    };

    queryPublisherV2 = (query: string) => {
      return getPublishersNames(this.props.session.publisher.ID, query);
    };

    renderUserInvitesPanel = () => {
      const { classes } = this.props;
      const hasRightToAssignRole =
        IS_AUTHORIZED(['roles_admin'], this.props.user.user).isAuthorized ||
        IS_AUTHORIZED(['invite_users'], this.props.user.user).isAuthorized;
      return (
        <Paper
          classes={{
            root: classes.paper,
          }}
        >
          {IS_AUTHORIZED(['invite_users'], this.props.user.user).isAuthorized && (
            <div>
              <Typography variant='h6' gutterBottom>
                Invite User
              </Typography>
              <Grid container alignItems='center'>
                <Grid item>
                  <Typography>Assign Role:</Typography>
                </Grid>
                <Grid item>
                  {this.renderInviteRoles(hasRightToAssignRole)}
                  {this.renderChangeRoleForInviteDialog()}
                </Grid>
              </Grid>
              <RowButton
                left={
                  <div className={classes.section}>
                    <TextField
                      label='Email Address'
                      placeholder='Add an email'
                      name='Email'
                      fullWidth
                      value={this.state.Email}
                      onChange={this.handleChange}
                    />
                    <div style={{ paddingTop: '20px' }}>
                      <Grid container alignItems='center'>
                        <Grid item>
                          <div style={{ marginRight: '5px' }}>Invite to:</div>
                        </Grid>
                        <Grid item>{this.props.session.publisher.Name}</Grid>
                      </Grid>
                    </div>
                  </div>
                }
                right={
                  <AsyncButtonV2
                    text='Invite'
                    inprogress={this.state.inviting || this.state.loading}
                    props={{
                      size: 'large',
                    }}
                    onClick={this.inviteUser}
                  />
                }
              />
            </div>
          )}
          <div style={{ marginTop: '30px' }}>
            {this.state.loading || this.state.userInviteLoading ? (
              <TableRow>
                <TableCell colSpan={5}>Loading users in your team...</TableCell>
              </TableRow>
            ) : (
              <div>
                {this.renderTableOfUsersInvite(this.state.usersInvite, hasRightToAssignRole)}
              </div>
            )}
          </div>
          <div style={{ marginTop: '2rem' }}></div>
        </Paper>
      );
    };

    renderUserPanel = () => {
      const { classes } = this.props;
      return (
        <Paper
          classes={{
            root: classes.paper,
          }}
        >
          <div style={{ marginTop: '30px' }}>
            {this.state.loading ? (
              <TableRow>
                <TableCell colSpan={5}>Loading users in your team...</TableCell>
              </TableRow>
            ) : (
              <div>
                {this.renderTableOfUsers(
                  this.state.usersFromUserToPublisherAssociation
                    .filter(
                      (user: IUserFromUserToPublisherAssociation) =>
                        !getDemoUsernames().includes(user.Email),
                    )
                    .map((user: IUserFromUserToPublisherAssociation) =>
                      obfuscateUserInfo(user, this.props.user.user.Username),
                    ),
                )}
              </div>
            )}
          </div>
          <div style={{ marginTop: '2rem' }}></div>
        </Paper>
      );
    };

    renderRoleManager = () => {
      return (
        <ModelRolesManager
          permissionsTemplate={this.state.permissionsTemplate}
          classes={this.props.classes}
          availableRoles={this.state.availableRoles}
          refreshAvailableRoles={this.getAvailableRoles}
          displayError={this.displayError}
          displaySuccess={this.displaySuccess}
          openCreateRoleDialog={() => this.handleToggleModal('openCreateRoleDialog', true)}
        />
      );
    };

    renderCreatePublisher = () => {
      // defines the new publisher form
      const isSuperAdmin = hasRole(this.props.user.user, [ROLE_SUPER]);
      const signupForm = ((): IFormItem[] => {
        const formBase: IFormItem[] = [
          {
            id: 'FirstName',
            label: 'First Name',
            required: true,
            render: FormItemTypeText,
          },
          {
            id: 'LastName',
            label: 'Last Name',
            required: true,
            render: FormItemTypeText,
          },
          {
            id: 'Company',
            label: 'Company',
            required: true,
            render: FormItemTypeText,
          },
          {
            id: 'Domain',
            label: 'Domain',
            required: true,
            render: FormItemTypeText,
          },
          {
            id: 'Email',
            label: 'Email',
            required: true,
            render: FormItemTypeText,
          },
          {
            id: 'Password',
            label: 'Password',
            required: true,
            render: FormItemTypePassword,
          },
          {
            id: 'Password2',
            label: 'Verify Password',
            required: true,
            render: FormItemTypePassword,
          },
        ];

        if (this.state.isNewRokuPublisher) {
          formBase.push(
            {
              id: 'RokuSeatID',
              label: 'Roku Seat ID',
              required: true,
              render: FormItemTypeText,
            },
            {
              id: 'RokuProviderChannelID',
              label: 'Roku Provider Channel ID',
              required: true,
              render: FormItemTypeText,
            },
          );
        }

        return formBase;
      })();

      return (
        <Grid style={{ maxWidth: 1000 }}>
          <section>
            <form onSubmit={this.handleSubmit}>
              <RenderForm
                schema={signupForm}
                data={this.state.data}
                errors={transformErrors(this.state.errors || {})}
                onUpdate={this.onUpdate}
              />
              {/* super users can toggle roku pub */}
              {isSuperAdmin && (
                <Grid
                  container
                  justify='flex-start'
                  direction='row'
                  alignItems='center'
                  style={{ marginTop: 10 }}
                >
                  <Grid item>
                    <Typography>Roku Publisher *</Typography>
                  </Grid>
                  <Grid item>
                    <Switch
                      checked={this.state.isNewRokuPublisher}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        this.setState({
                          isNewRokuPublisher: event.target.checked,
                        });
                      }}
                      color='secondary'
                    />
                  </Grid>
                </Grid>
              )}
              <div style={{ marginTop: '2rem' }}>
                <AsyncButtonV2
                  text='Create Publisher'
                  inprogress={this.state.showLoadingWheel}
                  onClick={this.createPublisher}
                />
              </div>
            </form>
          </section>
        </Grid>
      );
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const isSuperAdmin = hasRole(user.user, [ROLE_SUPER]);
      const isResellerAdmin = hasRole(user.user, [ROLE_RESELLER]);

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderCreateRoleDialog()}
            {this.renderChangeRoleForUserDialog()}
            {this.renderDeleteUsersDialog()}
            {this.renderChangeTimezoneForUserDialog()}
            <AppBar
              position='static'
              style={{ backgroundColor, boxShadow: 'none', color: 'black' }}
            >
              <Tabs
                value={this.state.tabIndex}
                onChange={(_: any, newIndex: number) => this.setState({ tabIndex: newIndex })}
                aria-label='simple tabs'
              >
                <Tab label='Team' {...MakeTabProps(0)} />
                <Tab label='Users Invites' {...MakeTabProps(1)} />
                <Tab label='Role & Permission' {...MakeTabProps(2)} />
                {(isSuperAdmin || isResellerAdmin) && (
                  <Tab label='Create Publisher *' {...MakeTabProps(3)} />
                )}
              </Tabs>
            </AppBar>
            <Divider />
            <TabPanel value={this.state.tabIndex} index={0}>
              {this.renderUserPanel()}
            </TabPanel>
            <TabPanel value={this.state.tabIndex} index={1}>
              {this.renderUserInvitesPanel()}
            </TabPanel>
            <TabPanel value={this.state.tabIndex} index={2}>
              {this.renderRoleManager()}
            </TabPanel>
            {(isSuperAdmin || isResellerAdmin) && (
              <TabPanel value={this.state.tabIndex} index={3}>
                {this.renderCreatePublisher()}
              </TabPanel>
            )}
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
